import compact from 'lodash/compact';
import findLastIndex from 'lodash/findLastIndex';
import upperFirst from 'lodash/upperFirst';
import type { Moment } from 'moment';

import {
  buildPrestationGroups,
  createCleaningBasketFromBooking,
  extractAddressFromBooking,
  extractAppointmentDateFromBooking,
  extractAppointmentFromBooking,
  extractBasketContentFromBooking,
  extractCleaningPrestationAndOptionsFromBooking,
  extractGiftCardFromBooking,
  extractPromotionFromBooking,
  formatBasketForAPI,
  getComparedBasketCategories,
  hasModificationsForCleaning,
  isCleaningPrestationAndOptionsSameAsBooking,
} from '@wecasa/booking-modification/src/utils/basket';

import type { AlertCommonProps } from '@wecasa/common/src/components/alert/types';
import * as PRESTATIONS from '@wecasa/common/src/constants/prestations';
import {
  FEET_PERMANENT_NAIL_POLISH,
  HANDS_GEL_NAIL_POLISH,
  MANICURE,
  PEDICURE,
} from '@wecasa/common/src/constants/prestations';
import type {
  Appointment,
  BasketContent,
  BasketContentItem,
  BasketPrices,
  CategoryReference,
  ChildcareAllowanceParams,
  ChildrenParams,
  GiftCard,
  PrestationReference,
  Promotion,
  Universe,
} from '@wecasa/common/src/types/commons';
import type { Booking, Potential, ProChoice, ProChoiceAPI } from '@wecasa/common/src/types/customer';
import { formatPrimaryAppointmentDeprecated } from '@wecasa/common/src/utils/appointment';
import { isRecurrentRecurrence } from '@wecasa/common/src/utils/cleaning';
import { FORMATS, formatDateDeprecated, formatDateForAPIDeprecated } from '@wecasa/common/src/utils/date';
import { getMessages } from '@wecasa/common/src/utils/errors';
import { getAllPrestationsForCategory } from '@wecasa/common/src/utils/prestations';
import RULES from '@wecasa/common/src/utils/prestations-rules';
import { isWanted } from '@wecasa/common/src/utils/previous-pros';
import { getDurationPerWeekFromSchedules } from '@wecasa/common/src/utils/schedules';
import {
  getChildcareAllowanceLevels,
  isChildcare,
  isChildcareReference,
  isHourlyBasedReference,
} from '@wecasa/common/src/utils/universes';

import { translate } from '@wecasa/i18n';

import type { BasketState } from '../ducks/types';
import type {
  BasketPotentialSource,
  ChildcareGeneratedPotential,
  CleaningGeneratedPotential,
  GeneratedPotential,
  RecurringChildcareGeneratedPotential,
} from '../types/basket';

const t = translate('prices');

type ShouldFormatPromotionPerHourParams = {
  promotion?: Promotion;
  duration?: BasketState['duration'];
};

export const shouldFormatPromotionPerHour = ({ promotion, duration }: ShouldFormatPromotionPerHourParams) =>
  promotion?.calculation_type === 'per_hour' && !duration;

type GetPromotionAlertContent = (params: { promotion?: Promotion; error?: Error | null }) => {
  type: Extract<AlertCommonProps['type'], 'success' | 'error'>;
  content: string | string[];
};

export const getPromotionAlertContent: GetPromotionAlertContent = ({ promotion, error }) => {
  if (promotion?.success) {
    return {
      type: 'success',
      content: t('components.promo_code.success', { code: promotion.code }),
    };
  }

  return {
    type: 'error',
    // Backend validation errors are in promotion.message, error is for unexpected errors
    content: compact([...(error ? getMessages(error) : []), promotion?.message]),
  };
};

type GetAppointmentDescriptionIfNoSecondaryProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
  isOnCalendarScreen?: boolean;
};

export const getAppointmentDescriptionIfNoSecondary = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
  isOnCalendarScreen = false,
}: GetAppointmentDescriptionIfNoSecondaryProps) => {
  const isRecurrent = isRecurrentRecurrence(weeklyRecurrence);
  const type = isRecurrent ? 'recurrent' : 'spot';
  if (primary) {
    return t(`basket.cleaning.appointment_hour.primary_with_no_secondary.${type}.description`);
  }
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.description`);
  }
  if (isOnCalendarScreen) {
    return t('basket.cleaning.appointment_calendar.date_selected_only.description');
  }
  if (!isRecurrent) {
    return t('basket.cleaning.appointment_hour.date_selected_only.spot.description');
  }
  if (weeklyRecurrence && weeklyRecurrence > 1) {
    return t('basket.cleaning.appointment_hour.date_selected_only.several.description');
  }
  return t('basket.cleaning.appointment_hour.date_selected_only.weekly.description');
};

type GetAppointmentAccordionTitleProps = {
  primary: Moment | null;
  appointmentDate?: Moment | null;
  weeklyRecurrence?: number | null;
};

export const getAppointmentAccordionTitle = ({
  primary,
  appointmentDate,
  weeklyRecurrence,
}: GetAppointmentAccordionTitleProps) => {
  const type = isRecurrentRecurrence(weeklyRecurrence) ? 'recurrent' : 'spot';

  // After booking creation
  if (primary) {
    return formatPrimaryAppointmentDeprecated(primary, weeklyRecurrence);
  }

  // Before booking creation (inside funnel), no primary yet selected
  if (!appointmentDate) {
    return t(`basket.cleaning.appointment_calendar.nothing_selected.${type}.title`);
  }

  // Before booking creation (inside funnel), primary selected for recurrent cleaning
  if (weeklyRecurrence) {
    const date = formatDateDeprecated(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH_SHORT);
    if (weeklyRecurrence === 0.25) {
      return t('appointment.primary.every_4_weeks', {
        first: date,
        day: formatDateDeprecated(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 0.5) {
      return t('appointment.primary.biweekly', {
        first: date,
        day: formatDateDeprecated(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence === 1) {
      return t('appointment.primary.weekly', {
        first: date,
        day: formatDateDeprecated(appointmentDate, FORMATS.DATE.WEEKDAY),
      });
    }
    if (weeklyRecurrence > 1) {
      return t('appointment.primary.several', { first: date, recurrence: weeklyRecurrence });
    }
  }

  return upperFirst(formatDateDeprecated(appointmentDate, FORMATS.DATE.WEEKDAY_DAY_MONTH));
};

export type HasBasketReachedMinimumPriceArgs = {
  basketContent: BasketState['basketContent'];
  currentPrice: number;
  minimumPrice: number;
  minPriceSurchargeAccepted: boolean;
};

export const hasBasketReachedMinimumPrice = ({
  basketContent,
  currentPrice,
  minimumPrice,
  minPriceSurchargeAccepted,
}: HasBasketReachedMinimumPriceArgs) => {
  if (!hasPrestations(basketContent)) return false;
  if (minPriceSurchargeAccepted) return true;

  return currentPrice >= minimumPrice;
};

// apply the rules for all the prestations (the one we just selected included)
export const applyRulesToPrestations = (category: string, prestations: string[], addedPrestation?: string) => {
  const rules = RULES[category];
  if (!rules?.length) return prestations;

  return rules.reduce((newPrestations, rule) => rule(newPrestations, addedPrestation), prestations);
};

export const canIncludePrestation = (prestation: string, basketContentItem: BasketContentItem) =>
  applyRulesToPrestations(
    basketContentItem.reference,
    [...basketContentItem.prestations, prestation],
    prestation,
  ).includes(prestation);

export const shouldNotRemovePrestation = (prestation: string, basketContentItem: BasketContentItem) =>
  (prestation === MANICURE &&
    basketContentItem.prestations.includes(MANICURE) &&
    basketContentItem.prestations.includes(HANDS_GEL_NAIL_POLISH)) ||
  (prestation === PEDICURE &&
    basketContentItem.prestations.includes(PEDICURE) &&
    basketContentItem.prestations.includes(FEET_PERMANENT_NAIL_POLISH));

/**
 * Adds or removes a prestation in the basket depending on a few business rules.
 * @param index The index of the targeted basket category in basketContent.
 * @param reference The reference of the prestation to be added.
 * @param isAdding A boolean indicating whether the prestation is added or not.
 * @param basketContent The current basketContent
 */
export const addOrRemovePrestation = (
  index: number,
  reference: PrestationReference,
  isAdding: boolean,
  basketContent: BasketContent,
) => {
  const basketCategory = basketContent[index];
  const basketPrestations = basketCategory.prestations;

  const addedPrestation = isAdding ? reference : undefined;

  const newPrestations = isAdding
    ? basketPrestations.concat(reference)
    : basketPrestations.filter((p) => p !== reference);

  return applyRulesToPrestations(basketCategory.reference, newPrestations, addedPrestation);
};

export const updateBasketContentByIndex = (
  index: number,
  reference: string,
  checked: boolean,
  basketContent: BasketContent,
) => [
  ...basketContent.slice(0, index),
  {
    ...basketContent[index],
    prestations: addOrRemovePrestation(index, reference, checked, basketContent),
  },
  ...basketContent.slice(index + 1, basketContent.length),
];

export const addCategory = (category: string, basketContent: BasketContent) => [
  ...basketContent,
  {
    reference: category,
    prestations: [],
  },
];

export const removeCategory = (category: string, basketContent: BasketContent) => {
  const lastIndex = findLastIndex(basketContent, (c) => c.reference === category);

  return lastIndex < 0
    ? basketContent
    : [...basketContent.slice(0, lastIndex), ...basketContent.slice(lastIndex + 1, basketContent.length)];
};

export const removeCategoriesFromBasket = (categories: CategoryReference[], basketContent: BasketContent) =>
  basketContent.filter((category) => !categories.includes(category.reference));

export const containsCategories = (categories: string[], basketContent: BasketContent) =>
  basketContent.some((category) => categories.includes(category.reference));

type UpdateAppointmentResult = {
  appointment?: Appointment;
  fieldsToUpdate: {} | { appointmentNear: boolean };
};

export const updateAppointment = (
  { primary, secondary }: Appointment,
  newAppointment: Moment,
  hasNearPro?: boolean,
  hasNearProToday?: boolean,
): UpdateAppointmentResult => {
  let appointment: Appointment;
  let fieldsToUpdate = {};

  if (!primary) {
    appointment = { primary: newAppointment, secondary: [] };

    if (hasNearPro) {
      fieldsToUpdate = { appointmentNear: true };
    } else if (hasNearProToday) {
      fieldsToUpdate = { appointmentNear: false };
    }
  } else if (newAppointment.isSame(primary)) {
    appointment = { primary: null, secondary: [] };
    fieldsToUpdate = { appointmentNear: null };
  } else if (secondary.some((s) => s.isSame(newAppointment))) {
    appointment = {
      primary,
      secondary: secondary.filter((secondaryAppointment) => !secondaryAppointment.isSame(newAppointment)),
    };
  } else {
    appointment = {
      primary,
      secondary: [...secondary, newAppointment],
    };
  }

  return {
    appointment,
    fieldsToUpdate,
  };
};

export const getFirstPrestationFromContent = (basketContent: BasketContent) => {
  const category = basketContent[0];
  return category ? category.prestations[0] : undefined;
};

export const isChildcareRecurring = (basketContent: BasketContent) =>
  getFirstPrestationFromContent(basketContent) === PRESTATIONS.RECURRING_CHILDCARE;

export const hasPrestations = (basketContent: BasketContent) =>
  basketContent.some((category) => category.prestations.length > 0);

// We will need this utils as long as the structure of the front pro choices is different
// than the structure of the back pro choices (sent on the booking).
// The future goal is to harmonise the front and back structures, so that these different types
// and conversion utils are no longer necessary.
export const extractWantedProChoicesFromBooking = (potential: Potential): ProChoice[] =>
  (potential.pro_choices || [])
    .map(({ pro, choice }: ProChoiceAPI) => {
      // The back does not send us the source attribute
      // So we force it to 'previous_pro'
      // This is safe as long the transformed pro choices are not sent down to the back
      const transformedChoice: ProChoice = {
        id: pro.id,
        source: 'previous_pro',
        pro,
        value: choice,
      };

      return transformedChoice;
    })
    .filter(isWanted);

export const extractChildcarePrestationFromBooking = (booking: Booking) => {
  const { universe, prestations } = booking;
  const isChildcareUniverse = isChildcareReference(universe);

  return isChildcareUniverse ? prestations[0][0] : undefined;
};

export const getCategoryTitleWithCount = (
  categoryIndex: number,
  categoryTitle: string,
  basketContent: BasketContent,
) => {
  const currentCatFilter = (cat: BasketContentItem) => cat.reference === basketContent[categoryIndex].reference;
  const currentCount =
    basketContent.filter(currentCatFilter).length > 1
      ? basketContent.slice(0, categoryIndex + 1).filter(currentCatFilter).length
      : undefined;
  return currentCount ? `${categoryTitle} ${currentCount}` : categoryTitle;
};

export const getBasketCategoryPrestationsList = (
  categoryReference: string,
  universe: Universe,
  prestationRefs: string[],
) => {
  const categoryPrestations = getAllPrestationsForCategory(categoryReference, universe);

  return prestationRefs.map((reference) => categoryPrestations.find((p) => p.reference === reference));
};

export const getCategoryCount = (basketContent: BasketContent, reference: CategoryReference) =>
  basketContent.filter((content) => content.reference === reference).length;

export const getTotalPrestationsCount = (basketContent: BasketContent) =>
  basketContent.reduce((sum, category) => sum + category.prestations.length, 0);

export const getBasketContentDuration = (basketContent: BasketContent, universe?: Universe) => {
  if (!universe) return 0;
  return basketContent.reduce((total, item) => {
    const prestations = getAllPrestationsForCategory(item.reference, universe);
    return (
      total +
      item.prestations.reduce(
        (sum, reference) => sum + (prestations.find((prestation) => prestation.reference === reference)?.duration ?? 0),
        0,
      )
    );
  }, 0);
};

export const hasGiftCard = (giftCard?: GiftCard | {}) => Boolean(giftCard && 'code' in giftCard && giftCard.code);

type ChildcareAllowanceAmountParams = {
  universe?: Universe;
  basketContent: BasketContent;
  childcareAllowanceParams: ChildcareAllowanceParams;
  childrenParams: ChildrenParams;
};

export const getChildcareAllowanceAmount = ({
  universe,
  basketContent,
  childcareAllowanceParams,
  childrenParams,
}: ChildcareAllowanceAmountParams) => {
  const isReccurent = isChildcareRecurring(basketContent);
  if (!universe || !isChildcare(universe) || !isReccurent) return 0;

  const { maritalStatus, incomeLevel } = childcareAllowanceParams;

  const levels = getChildcareAllowanceLevels({
    universe,
    maritalStatus,
    childrenCount: childrenParams.childrenCount,
    youngestChildAge: childrenParams.youngestChildAge,
  });

  if (!levels) return 0;

  const level = levels.find((l) => l.type === incomeLevel);
  return !level ? 0 : level.amount || 0;
};

const formatClosedTimeSlots = (closedTimeSlots: Moment[]) =>
  closedTimeSlots ? JSON.stringify(closedTimeSlots.map(formatDateForAPIDeprecated)) : undefined;

export const buildBasketPrimaryAppointment = ({ appointment }: Pick<BasketPotentialSource, 'appointment'>) =>
  appointment.primary ? formatDateForAPIDeprecated(appointment.primary) : '';

export const buildBasketSecondaryAppointments = ({ appointment }: Pick<BasketPotentialSource, 'appointment'>) =>
  appointment.secondary.map(formatDateForAPIDeprecated).join(',');

type BuildBasketPotentialParams = {
  prices: BasketPrices | null;
  basket: BasketPotentialSource;
  hasMembership?: boolean;
};

export const buildBasketPotential = ({
  prices,
  basket,
  hasMembership,
}: BuildBasketPotentialParams): GeneratedPotential => {
  const {
    universe,
    basketContent,
    duration,
    appointment,
    appointmentNear,
    customer,
    address,
    paymentInfos: { stripePaymentMethodId, stripePaymentIntentId, stripeSetupIntentId, preauthorizationSkipped },
    comments,
    proGender,
    hasPets,
    closedTimeSlots: { minAppointmentDelay, all: closedTimeSlots, fetchedAt },
    activities,
    childrenParams,
    childcareAllowanceParams,
    schedules,
    includeHolidays,
    promotion,
    giftCard,
    weekly_recurrence: weeklyRecurrence,
    zone,
    childcareHoursPerWeek,
  } = basket;
  const primaryAppointment = buildBasketPrimaryAppointment({ appointment });
  const otherDisponibilities = buildBasketSecondaryAppointments({ appointment });
  const withGiftCard = !promotion?.code && hasGiftCard(giftCard);

  // TODO replace any with a type specific for the parameter with send to the create potential api
  const potential: GeneratedPotential = {
    ...customer,
    ...address,
    // @ts-ignore
    universe,
    zone,
    comments,
    weekly_recurrence: weeklyRecurrence,
    prestation_groups: buildPrestationGroups(basket),
    pro_gender: proGender,
    appointment: primaryAppointment,
    other_disponibilities: otherDisponibilities,
    appointment_near: appointmentNear,
    stripe_payment_method_id: stripePaymentMethodId,
    stripe_payment_intent_id: stripePaymentIntentId,
    stripe_setup_intent_id: stripeSetupIntentId,
    preauthorization_skipped: preauthorizationSkipped,
    remember_credit_card: true,
    closed_time_slots: formatClosedTimeSlots(closedTimeSlots),
    promo_code: promotion.success ? promotion.code : '',
    has_pets: hasPets,
    min_appointment_delay: minAppointmentDelay,
    zip_code_submitted_at: fetchedAt ? formatDateForAPIDeprecated(fetchedAt) : undefined,
    // Address needs to be set when this method is called
    country_code: address!.country_code,
  };

  if (prices) {
    const hasMembershipPrices = hasMembership && (prices.kind === 'wellness' || prices.kind === 'cleaning');
    const totalPrice = hasMembershipPrices ? prices.membership.totalPrice : prices.totalPrice;
    const promoValue = hasMembershipPrices ? prices.membership.promoValue : prices.promoValue;
    const giftValue = hasMembershipPrices ? prices.membership.giftValue : prices.giftValue;

    potential.promo_value = withGiftCard ? giftValue : promoValue;
    potential.gift_code = withGiftCard ? (giftCard as GiftCard).code : '';
    // We ensure the total price (in cents) is always an integer.
    potential.total_price = Math.round(totalPrice);
  }

  /**
   * Only for cleaning the frontend needs to calculate the duration and communicate it
   * to the server. For all other universes we avoid passing the duration as it does
   * not get properely re-calculated by us and if we sent a wrong duration value,
   * the server could complain.
   */
  if (isHourlyBasedReference(universe)) {
    (potential as ChildcareGeneratedPotential | CleaningGeneratedPotential).duration = duration;
  }

  if (isChildcareReference(universe)) {
    (potential as ChildcareGeneratedPotential).children_count = childrenParams.childrenCount;
    (potential as ChildcareGeneratedPotential).youngest_child_age = childrenParams.youngestChildAge;
    (potential as ChildcareGeneratedPotential).youngest_child_born_on = childrenParams.youngestBirthdate;
    (potential as ChildcareGeneratedPotential).activities = activities;
    // Backend wants all data or nothing : incomeLevel is the last data we get in allowance screen
    if (childcareAllowanceParams.incomeLevel) {
      (potential as ChildcareGeneratedPotential).marital_status = childcareAllowanceParams.maritalStatus;
      (potential as ChildcareGeneratedPotential).income_level = childcareAllowanceParams.incomeLevel;
    }
    (potential as ChildcareGeneratedPotential).schedules = schedules;
    (potential as ChildcareGeneratedPotential).include_holidays = includeHolidays;

    // This is a temporary solution demanded by the backend and could probably change in future
    if (isChildcareRecurring(basketContent) && prices && prices.kind === 'childcare') {
      (potential as RecurringChildcareGeneratedPotential).estimated_weekly_hours = childcareHoursPerWeek;
      (potential as RecurringChildcareGeneratedPotential).duration = childcareHoursPerWeek
        ? childcareHoursPerWeek * 60
        : getDurationPerWeekFromSchedules(schedules);
      // We ensure the total price (in cents) is always an integer.
      (potential as RecurringChildcareGeneratedPotential).total_price = Math.round(prices.weeklyTotalPrice);
    }
  }

  return potential;
};

/*
 * Re-exported for the creation funnel, which still builds a basket from an existing booking.
 * @wecasa/booking-modification holds the single definition of each; these go away with the legacy
 * edit modes.
 */
export {
  buildPrestationGroups,
  createCleaningBasketFromBooking,
  extractAddressFromBooking,
  extractAppointmentDateFromBooking,
  extractAppointmentFromBooking,
  extractBasketContentFromBooking,
  extractCleaningPrestationAndOptionsFromBooking,
  extractGiftCardFromBooking,
  extractPromotionFromBooking,
  formatBasketForAPI,
  getComparedBasketCategories,
  hasModificationsForCleaning,
  isCleaningPrestationAndOptionsSameAsBooking,
};
